import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  useTheme as useMuiTheme,
} from "@mui/material";
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useTheme } from "../contexts/ThemeContext";
import {
  subscribeToEmailSyncStatus,
  EmailSyncStatusResponse,
} from "../services/emailService";
import { STORAGE_KEYS, EMAIL_SYNC } from "../helpers/constants";
import { getFetchErrorMessage } from "../helpers/errorUtils";
import { formatSyncTime, formatSyncTimeWithDirection } from "../helpers/date";

// Interface for email sync status state
export interface EmailSyncStatusState {
  progress_percentage: number;
  sync_status: string;
  is_active: boolean;
  thread_count: number;
  last_sync: string | null;
  /** Next scheduled sync time (ISO string) */
  next_sync?: string | null;
}

// Props interface
export interface EmailSyncStatusIndicatorProps {
  channelData: {
    name?: string;
    channel_type?: 'email' | 'message' | 'voice';
  } | null;
  channelId?: string;
  onSyncComplete?: () => Promise<void>;
  /** Called when sync-in-progress state changes so parent can hide duplicate empty-state copy */
  onSyncInProgressChange?: (inProgress: boolean) => void;
  hasThreads?: boolean;
  showEmptyState?: boolean; // Whether to show the empty state message when no threads
}

const EmailSyncStatusIndicator: React.FC<EmailSyncStatusIndicatorProps> = ({
  channelData,
  channelId,
  onSyncComplete,
  onSyncInProgressChange,
  hasThreads = false,
  showEmptyState = false,
}) => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();

  // State for email sync status
  const [emailSyncStatus, setEmailSyncStatus] = useState<EmailSyncStatusState | null>(null);
  const [emailSyncLoading, setEmailSyncLoading] = useState<boolean>(true);
  const [emailSyncError, setEmailSyncError] = useState<string | null>(null);
  const [hasReloadedForSync, setHasReloadedForSync] = useState<boolean>(false);
  const [lastSyncTime, setLastSyncTime] = useState<{
    relative: string;
    exact: string;
  }>({ relative: "", exact: "" });
  const [nextSyncTime, setNextSyncTime] = useState<{
    relative: string;
    exact: string;
  }>({ relative: "—", exact: "—" });

  // Effect to subscribe to the email sync status polling service
  useEffect(() => {
    if (!channelData?.name || channelData.channel_type !== 'email') {
      setEmailSyncLoading(false);
      onSyncInProgressChange?.(false);
      return;
    }

    setEmailSyncLoading(true);
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";

    const handleSyncStatusUpdate = (
      status: EmailSyncStatusResponse | null,
      error: Error | null
    ) => {
      setEmailSyncLoading(false);
      if (error) {
        setEmailSyncError(getFetchErrorMessage(error, "Failed to fetch email sync status"));
        onSyncInProgressChange?.(false);
        return;
      }

      if (status) {
        setEmailSyncError(null);
        const syncData = status.data.email_sync;
        const threadCount = status.data?.data_counts?.threads ?? 0;
        // API returns "in_progress" (or "pending") while syncing; treat both as syncing state
        const inProgress =
          syncData.sync_status === "pending" || syncData.sync_status === "in_progress";
        onSyncInProgressChange?.(inProgress);
        setEmailSyncStatus({
          progress_percentage: syncData.progress_percentage,
          sync_status: syncData.sync_status,
          is_active: syncData.is_active,
          thread_count: threadCount,
          last_sync: syncData.last_sync,
          next_sync: syncData.next_sync ?? null,
        });

        // Trigger onSyncComplete when sync is finished (API may use "in_progress" or "pending")
        const wasInProgress =
          syncData.sync_status === "pending" || syncData.sync_status === "in_progress";
        if (
          (threadCount >= EMAIL_SYNC.THREAD_COMPLETION_PERCENTAGE ||
            syncData.progress_percentage >= EMAIL_SYNC.COMPLETION_PERCENTAGE) &&
          wasInProgress &&
          !hasReloadedForSync
        ) {
          setHasReloadedForSync(true);
          setTimeout(async () => {
            if (!hasThreads && onSyncComplete) {
              await onSyncComplete();
            }
          }, 2500);
        }

        // Reset reload flag if a new sync starts
        if (threadCount < 5 || syncData.progress_percentage < 5) {
          setHasReloadedForSync(false);
        }
      }
    };

    const unsubscribe = subscribeToEmailSyncStatus(
      channelData.name,
      token,
      handleSyncStatusUpdate,
      channelData.channel_type
    );

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelData?.name, channelData?.channel_type]);

  // Effect to update relative time for last sync
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    // Show last-sync time when sync is completed (success) or when we have a last_sync value
    if (
      (emailSyncStatus?.sync_status === "success" || emailSyncStatus?.sync_status === "in_progress") &&
      emailSyncStatus.last_sync
    ) {
      const updateLastSyncTime = () => {
        setLastSyncTime(formatSyncTime(emailSyncStatus.last_sync));
      };
      updateLastSyncTime(); // Initial call
      interval = setInterval(updateLastSyncTime, 60000); // Update every minute
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [emailSyncStatus?.sync_status, emailSyncStatus?.last_sync]);

  // Treat both "pending" and "in_progress" as syncing for display
  const isSyncing =
    emailSyncStatus?.sync_status === "pending" ||
    emailSyncStatus?.sync_status === "in_progress";
  const isSyncSuccess = emailSyncStatus?.sync_status === "success";

  // Effect to format and periodically update next sync time (for tooltip + label)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (emailSyncStatus?.next_sync) {
      const updateNextSyncTime = () => {
        setNextSyncTime(formatSyncTimeWithDirection(emailSyncStatus.next_sync ?? null));
      };
      updateNextSyncTime();
      interval = setInterval(updateNextSyncTime, 60000);
    } else {
      setNextSyncTime({ relative: "—", exact: "—" });
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [emailSyncStatus?.next_sync]);

  // Header indicator component
  const HeaderIndicator = () => {
    if (emailSyncLoading && !emailSyncStatus) {
      return <CircularProgress size={20} />;
    }
    
    if (!emailSyncStatus) return null;

    return (
      <>
        {!emailSyncStatus.is_active ? (
          <Chip
            data-testid="email-sync-status-disconnected"
            icon={<ErrorIcon sx={{ color: "white", fontSize: 16 }} />}
            label="Disconnected"
            size="small"
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 600,
              bgcolor: "error.main",
              color: "white",
              "& .MuiChip-label": {
                px: 1,
                color: "white",
              },
              "& .MuiChip-icon": {
                color: "white",
              },
            }}
          />
        ) : (
          <Tooltip
            title={
              isSyncing ? (
                `Syncing emails... ${emailSyncStatus.progress_percentage}% complete`
              ) : isSyncSuccess ? (
                <Box component="span" sx={{ display: "block", whiteSpace: "pre-line" }}>
                  Last sync: {lastSyncTime.exact}
                  {nextSyncTime.exact !== "—" && (
                    <>
                      {"\n\n"}
                      Next sync: {nextSyncTime.exact}
                    </>
                  )}
                </Box>
              ) : (
                "Email sync failed"
              )
            }
            placement="bottom"
            arrow
          >
            <Chip
              data-testid={`email-sync-status-${emailSyncStatus.sync_status}`}
              icon={
                isSyncing ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 16,
                      height: 16,
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  >
                    <SyncIcon sx={{ fontSize: 16 }} />
                  </Box>
                ) : isSyncSuccess ? (
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 16 }} />
                )
              }
              label={
                isSyncing
                  ? `Syncing... ${emailSyncStatus.progress_percentage}%`
                  : isSyncSuccess
                  ? `Sync completed. Last sync: ${lastSyncTime.relative}${nextSyncTime.relative !== "—" ? ` · Next: ${nextSyncTime.relative}` : ""}`
                  : "Sync Failed"
              }
              color={
                isSyncing
                  ? "warning"
                  : isSyncSuccess
                  ? "success"
                  : "error"
              }
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                fontWeight: 500,
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
          </Tooltip>
        )}
      </>
    );
  };

  // Empty state message component
  const EmptyStateMessage = () => {
    if (!showEmptyState || !emailSyncStatus || !isSyncing) {
      return null;
    }

    return (
      <>
        <CircularProgress
          size={48}
          sx={{ color: muiTheme.palette.primary.main, mb: 2 }}
        />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Email Sync in Progress
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", lineHeight: 1.5 }}>
          New conversations will appear automatically once the sync is complete.
        </Typography>
      </>
    );
  };

  // If showEmptyState is true, render the empty state message
  // Otherwise, render the header indicator
  if (showEmptyState) {
    return <EmptyStateMessage />;
  }

  return <HeaderIndicator />;
};

export default EmailSyncStatusIndicator;
